import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { CheckBoxComp, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { SettingDTO, ListFilterDTO, UserDTO, ValueListDTO, ValueListFilterDTO, CompanyDTO, CompanyFilterDTO, AccessRoleDTO, AccessRoleFilterDTO, UserFilterDTO, ApplicationFilterDTO, FeatureFilterDTO, ApplicationDTO, FeatureDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface ISettingMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const SettingMaintenance: React.FC<ISettingMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [settingDTO, setSettingDTO] = useState<SettingDTO>();
    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');


    useEffect(() => {
        setSettingDTO({
            ...settingDTO,
            id: '',
            name: '',
            description: '',
            value: '',
            applicationId: '',
            featureId: '',
            isGlobal: false,
            isSecret: false,
            companyId: userInfo.user.companyId
        } as SettingDTO);
    }, [data]);

    useEffect(() => {
        if (isOpenMaintenance) {
            handleUserGetAllAsync(setUserList);
            handleCompanyGetAll();
            handleGetSettingByIdAsync();
            handleApplicationGetAllAsync();

            handleGetPermission(ListId.parmsphere.settingId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        setIsDisabled(settingDTO?.isGlobal ?? false);
    }, [settingDTO?.isGlobal]);

    useEffect(() => {
        handleFeatureGetAllViewAsync();
    }, [settingDTO?.applicationId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleApplicationGetAllAsync = useCallback(async () => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setApplicationList, showMessage);
    }, []);

    const handleFeatureGetAllViewAsync = useCallback(async () => {
        if(settingDTO?.applicationId === '' || settingDTO?.applicationId === undefined)
            return;

        const featureFilterDTO: FeatureFilterDTO = {
            applicationId: settingDTO?.applicationId
        };
        GenericService.GetAllViewAsync1('appsphere.feature', featureFilterDTO, setActionExecuted, setLoading, setFeatureList, showMessage);
    }, [settingDTO?.applicationId]);

    const handleGetSettingByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('parmsphere.setting', data.id, setLoading, setSettingDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = settingDTO as SettingDTO;

        if (settingDTO?.id === '' || settingDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('parmsphere.setting', dto, setSettingDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('parmsphere.setting', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [settingDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='SETTING DETAILS'
                icon='list_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                placeholder='Company'
                                value={settingDTO?.companyId}
                                setValue={setSettingDTO}
                                obj={settingDTO}
                                valueList={companyList}
                                required={false}
                                hasNoneSelect={true}
                                disabled={isDisabled}
                                searchable={true}
                                labelKey='name'
                                valueKey='id'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='applicationId'
                                label='Application Name'
                                setValue={setSettingDTO}
                                value={settingDTO?.applicationId}
                                obj={settingDTO}
                                valueList={applicationList}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='featureId'
                                label='Feature Name'
                                setValue={setSettingDTO}
                                value={settingDTO?.featureId}
                                obj={settingDTO}
                                valueList={featureList}
                            />
                        </Grid>
                        <Grid item xs={12} md={11}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={settingDTO?.name}
                                setValue={setSettingDTO}
                                obj={settingDTO}
                                required={true}
                                maxLength={100}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <CheckBoxComp
                                id='isGlobal'
                                label='Global'
                                setValue={setSettingDTO}
                                objDTO={settingDTO}
                                defaultChecked={settingDTO?.isGlobal}
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={11}>
                            <TextFieldInput
                                id='value'
                                label='Value'
                                value={settingDTO?.value}
                                setValue={setSettingDTO}
                                obj={settingDTO}
                                maxLength={400}
                                multiline={false}
                                type={settingDTO?.isSecret ? 'password' : undefined}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <CheckBoxComp
                                id='isSecret'
                                label='Secret'
                                setValue={setSettingDTO}
                                objDTO={settingDTO}
                                defaultChecked={settingDTO?.isSecret}
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={settingDTO?.description}
                                setValue={setSettingDTO}
                                obj={settingDTO}
                                required={true}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {settingDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={settingDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === settingDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(settingDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === settingDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(settingDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};